import React, { useEffect, useMemo, useState } from 'react';
import { Zap, Plus, Trash2, Save, PhoneCall, ListPlus, AlertTriangle, Ban } from 'lucide-react';
import {
  replyRulesService, ReplyRule, ReplyRuleAction, ReplyMatch,
} from 'api/services/replyRules.service';
import { whatsappService } from 'api/services/whatsapp.service';
import { aiAgentsService } from 'api/services/aiAgents.service';
import { voiceCampaignsService } from 'api/services/voiceCampaigns.service';
import { campaignLabel, campaignLabelById } from './campaignLabel';

/**
 * "When somebody does this on WhatsApp, do that."
 *
 * The page is deliberately not an "auto-call interested leads" screen. Interest
 * is one rule; the same form writes "Reschedule → enrol them in the follow-up
 * campaign" without any new UI.
 */

const MATCH_LABEL: Record<ReplyMatch, string> = {
  BUTTON_PAYLOAD: 'Button (by payload / id)',
  BUTTON_TEXT: 'Button (by visible label)',
  LIST_ID: 'List reply (by id)',
  KEYWORD: 'Keyword in a typed reply',
  ANY_REPLY: 'Any reply at all',
  LEAD_CAPTURED: 'A lead was captured',
};

const MATCH_HINT: Record<ReplyMatch, string> = {
  BUTTON_PAYLOAD:
    'The stable one. Use the payload you set on the template button — it survives the copy being reworded or translated.',
  BUTTON_TEXT:
    'Only for templates built without a payload. Matches the visible label, so this rule stops firing the day somebody improves the wording.',
  LIST_ID: 'The id of the list row the person picked.',
  KEYWORD: 'Whole words only — a rule on “no” will not fire on “notes”.',
  ANY_REPLY: 'Anything they send. Pair it with a campaign, or it fires on every inbound message.',
  LEAD_CAPTURED:
    'Name the origin: WEB for web forms, POST_CALL for leads recovered after a call. Leave it blank and it fires on every lead — including the ones campaign calls produce.',
};

const STATUS_STYLE: Record<ReplyRuleAction['status'], string> = {
  DONE: 'bg-green-100 text-green-800',
  PENDING: 'bg-amber-100 text-amber-800',
  SKIPPED: 'bg-gray-100 text-gray-700',
  FAILED: 'bg-red-100 text-red-800',
};

const blank = (): Partial<ReplyRule> => ({
  name: '',
  enabled: true,
  campaignId: null,
  matchType: 'BUTTON_PAYLOAD',
  matchValue: '',
  action: 'AI_CALL',
  agentId: null,
  fromNumber: null,
  voiceCampaignId: null,
  callerNameFallback: null,
  oncePerContact: true,
});


/**
 * What the agent is told about the person before it says hello.
 *
 * <p>This list is not decoration. Every value is resolved in cpass and travels
 * as a concrete string — the agent is NEVER handed "Hello {first_name}" to
 * substitute, because this estate has proof that fails and reaches the customer.
 * So the prompt has to be written knowing which facts will actually be there,
 * and until now that set existed only in the code.
 */
const AGENT_FIELDS: Array<{ key: string; from: string }> = [
  { key: 'first_name', from: 'Always present. Their name, or the fallback below.' },
  { key: 'full_name', from: 'The contact’s full name, when we hold one.' },
  { key: 'company', from: 'The contact’s company.' },
  { key: 'designation', from: 'The contact’s job title.' },
  { key: 'reply_text', from: 'What they actually sent — the reason for the call.' },
  { key: 'reply_trigger', from: 'The button or keyword that matched this rule.' },
];

const AgentKnows: React.FC = () => (
  <div className="rounded border border-gray-200 dark:border-gray-700 p-3 text-xs">
    <p className="font-medium text-gray-800 dark:text-gray-200 mb-1">What the agent is told</p>
    <p className="text-gray-500 mb-2">
      Each of these reaches the agent as a real value, already filled in — never as a
      placeholder for it to substitute. Anything we do not know is simply left out,
      so a prompt that mentions it degrades to a missing word rather than a hole.
    </p>
    <dl className="space-y-1">
      {AGENT_FIELDS.map((f) => (
        <div key={f.key} className="flex gap-2">
          <dt className="font-mono text-gray-700 dark:text-gray-300 w-32 shrink-0">{f.key}</dt>
          <dd className="text-gray-500">{f.from}</dd>
        </div>
      ))}
      <div className="flex gap-2">
        <dt className="font-mono text-gray-700 dark:text-gray-300 w-32 shrink-0">…your own</dt>
        <dd className="text-gray-500">
          Every custom field on the contact, under its own name — including the columns
          you kept when you uploaded the campaign CSV. There is no fixed list: name a
          column <span className="font-mono">Preferred Call Time</span> and the agent
          receives <span className="font-mono">preferred_call_time</span>.
        </dd>
      </div>
    </dl>
  </div>
);

const WhatsAppReplyRules: React.FC = () => {
  const [rules, setRules] = useState<ReplyRule[]>([]);
  const [actions, setActions] = useState<ReplyRuleAction[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [voiceCampaigns, setVoiceCampaigns] = useState<any[]>([]);
  const [draft, setDraft] = useState<Partial<ReplyRule> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    const [r, a] = await Promise.all([replyRulesService.list(), replyRulesService.actions(50)]);
    if (r.success && Array.isArray(r.data)) setRules(r.data);
    if (a.success && Array.isArray(a.data)) setActions(a.data);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      await load();
      // The pickers. Each is optional to the page working — a failure here leaves
      // a free-text id rather than blocking the whole screen.
      const c = await whatsappService.getCampaigns({ size: 200 });
      const list = (c as any)?.data?.content || (c as any)?.data?.data || [];
      if (Array.isArray(list)) setCampaigns(list);
      const ag = await aiAgentsService.list();
      if (ag.success && Array.isArray(ag.data)) setAgents(ag.data);
      const vc = await voiceCampaignsService.list();
      if (vc.success && Array.isArray(vc.data)) setVoiceCampaigns(vc.data);
    })();
  }, []);

  const ruleName = useMemo(() => {
    const m = new Map(rules.map((r) => [r.id, r.name]));
    return (id: string) => m.get(id) || id.slice(0, 8);
  }, [rules]);

  /**
   * A campaign's name, falling back to its id.
   *
   * The id is not useless — it is what a rule stores and what the API filters
   * on — but nobody recognises a campaign by uuid, and a rule that says "on
   * b43f60d1…" cannot be checked at a glance against the campaign somebody
   * actually sent.
   */
  const campaignName = useMemo(() => campaignLabelById(campaigns), [campaigns]);

  const voiceCampaignName = useMemo(() => campaignLabelById(voiceCampaigns), [voiceCampaigns]);

  const set = (patch: Partial<ReplyRule>) => setDraft((d) => ({ ...(d || {}), ...patch }));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setErr(null);
    setMsg(null);
    const body: Partial<ReplyRule> = {
      ...draft,
      // ANY_REPLY carries no value; sending a stale one would be stored and then
      // ignored, which reads as a rule that is narrower than it is.
      matchValue: draft.matchType === 'ANY_REPLY' ? null : (draft.matchValue || '').trim() || null,
      agentId: draft.action === 'AI_CALL' ? draft.agentId || null : null,
      voiceCampaignId: draft.action === 'ENROL_CAMPAIGN' ? draft.voiceCampaignId || null : null,
      // Only for the actions that read them, so switching action does not leave
      // a stale template id on a rule that now places a call.
      templateId: draft.action === 'SEND_WHATSAPP' || draft.action === 'SEND_EMAIL'
        ? draft.templateId || null : null,
      composePrompt: draft.action === 'SEND_EMAIL' ? (draft.composePrompt || '').trim() || null : null,
    };
    const res = draft.id
      ? await replyRulesService.update(draft.id, body)
      : await replyRulesService.create(body);
    setSaving(false);
    if (!res.success) {
      // The API refuses a rule that could never fire, and its message names which
      // half is missing — far more useful than "save failed".
      setErr(res.message || 'Could not save the rule');
      return;
    }
    setDraft(null);
    setMsg('Saved');
    load();
  };

  const remove = async (id: string) => {
    const res = await replyRulesService.remove(id);
    if (res.success) load();
    else setErr(res.message || 'Could not delete the rule');
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const cls = 'w-full border rounded px-3 py-2 text-sm';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Zap size={20} /> Reply rules
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            When somebody taps a button or replies to a campaign, act on it — ring them with an AI
            agent, or add them to a voice campaign. A reply already knows which campaign it answers,
            so a rule only has to say what to do about it.
          </p>
        </div>
        {!draft && (
          <button className="border rounded px-3 py-2 text-sm flex items-center gap-2"
                  onClick={() => { setDraft(blank()); setErr(null); setMsg(null); }}>
            <Plus size={16} /> New rule
          </button>
        )}
      </div>

      {msg && <div className="text-sm text-green-700">{msg}</div>}
      {err && (
        <div className="text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {err}
        </div>
      )}

      {draft && (
        <div className="border rounded p-4 space-y-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">Name</span>
              <input className={cls} value={draft.name || ''}
                     placeholder="Interested → call now"
                     onChange={(e) => set({ name: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">Campaign</span>
              <select className={cls} value={draft.campaignId || ''}
                      onChange={(e) => set({ campaignId: e.target.value || null })}>
                <option value="">Any campaign</option>
                {campaigns.map((c: any) => (
                  <option key={c.id} value={c.id}>{campaignLabel(c)}</option>
                ))}
              </select>
              <span className="block mt-1 text-[11px] text-gray-400">
                “Any” is the only setting that also sees a message somebody sends out of the blue.
              </span>
            </label>

            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">Fires on</span>
              <select className={cls} value={draft.matchType}
                      onChange={(e) => set({ matchType: e.target.value as ReplyMatch })}>
                {(Object.keys(MATCH_LABEL) as ReplyMatch[]).map((m) => (
                  <option key={m} value={m}>{MATCH_LABEL[m]}</option>
                ))}
              </select>
              <span className="block mt-1 text-[11px] text-gray-400">
                {MATCH_HINT[draft.matchType as ReplyMatch]}
              </span>
            </label>
            {draft.matchType !== 'ANY_REPLY' && (
              <label className="block">
                <span className="block text-xs text-gray-500 mb-1">Matching</span>
                <input className={cls} value={draft.matchValue || ''}
                       placeholder={draft.matchType === 'KEYWORD' ? 'interested'
                         : draft.matchType === 'LEAD_CAPTURED' ? 'WEB'
                         : 'OPT_IN_CALL'}
                       onChange={(e) => set({ matchValue: e.target.value })} />
              </label>
            )}

            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">Then</span>
              <select className={cls} value={draft.action}
                      onChange={(e) => set({ action: e.target.value as any })}>
                <option value="AI_CALL">Call them now with an AI agent</option>
                <option value="ENROL_CAMPAIGN">Add them to a voice campaign</option>
                <option value="SEND_WHATSAPP">Send them a WhatsApp template</option>
                <option value="SEND_EMAIL">Send them an email</option>
                <option value="OPT_OUT">Stop contacting them</option>
              </select>
            </label>

            {draft.action === 'OPT_OUT' && (
              <p className="text-xs text-gray-500 bg-gray-50 border rounded p-2">
                Puts them on the do-not-contact list permanently, for calls as well as messages —
                somebody asking to be left alone means the phone too. Nothing else is needed: no
                agent, no campaign.
                <br /><br />
                <b>“STOP” is already handled without a rule</b> — and before any rule runs, so this
                is for what that cannot know: an Unsubscribe button’s payload, or the words your
                customers actually use.
              </p>
            )}

            {(draft.action === 'SEND_WHATSAPP' || draft.action === 'SEND_EMAIL') && (
              <div className="space-y-2">
                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">
                    {draft.action === 'SEND_WHATSAPP' ? 'WhatsApp template' : 'Email template'}
                  </span>
                  <input className={cls} value={draft.templateId || ''}
                         placeholder="template id"
                         onChange={(e) => set({ templateId: e.target.value })} />
                </label>

                {draft.action === 'SEND_WHATSAPP' && (
                  <p className="text-xs text-gray-500 bg-gray-50 border rounded p-2">
                    A template, because it has to be. Outside WhatsApp's 24-hour window Meta
                    accepts nothing else from a business that speaks first — and a lead who filled
                    in a form has never messaged you, so every send here is outside it.
                  </p>
                )}

                {draft.action === 'SEND_EMAIL' && (
                  <>
                    <label className="block">
                      <span className="block text-xs text-gray-500 mb-1">
                        Or write it — what should the mail say?
                      </span>
                      <textarea className={cls} rows={4} value={draft.composePrompt || ''}
                                placeholder="Reply as the sales team. Answer what they asked about, offer a time, keep it to four sentences."
                                onChange={(e) => set({ composePrompt: e.target.value })} />
                    </label>
                    <p className="text-xs text-gray-500 bg-gray-50 border rounded p-2">
                      Leave this blank and the template is sent as written. Fill it in and the mail
                      is composed from what the lead actually told you — their answers are supplied
                      alongside this instruction, so describe the job rather than repeating the
                      data.
                      <br /><br />
                      <b>Name a template as well.</b> It is what goes out if the model is
                      unavailable, so an outage costs a better mail rather than any mail.
                    </p>
                  </>
                )}
              </div>
            )}

            {draft.action === 'AI_CALL' ? (
              <>
                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">Agent</span>
                  <select className={cls} value={draft.agentId || ''}
                          onChange={(e) => set({ agentId: e.target.value || null })}>
                    <option value="">Choose an agent…</option>
                    {agents.map((a: any) => (
                      <option key={a.id} value={String(a.id)}>{a.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">Caller ID</span>
                  <input className={cls} value={draft.fromNumber || ''}
                         placeholder="e.g. 912265197304"
                         onChange={(e) => set({ fromNumber: e.target.value || null })} />
                  {/* It used to say "optional — chosen automatically". Proven
                      otherwise on a live probe: with none set the call is refused
                      at dial time with "a caller ID is required", and the rule
                      fails hours later on somebody's reply rather than here. It
                      IS chosen automatically for an organisation that holds a
                      number; an agent whose organisation holds none — Atmantan is
                      one — has nothing to choose from. */}
                  <span className="block mt-1 text-[11px] text-amber-600 dark:text-amber-500">
                    Leave blank only if this agent’s organisation owns a number. If it does not,
                    the call is refused when the rule fires, not when you save it.
                  </span>
                </label>
                <AgentKnows />
              </>
            ) : (
              <label className="block">
                <span className="block text-xs text-gray-500 mb-1">Voice campaign</span>
                <select className={cls} value={draft.voiceCampaignId || ''}
                        onChange={(e) => set({ voiceCampaignId: e.target.value || null })}>
                  <option value="">Choose a campaign…</option>
                  {voiceCampaigns.map((c: any) => (
                    <option key={c.id} value={c.id}>{campaignLabel(c)}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <label className="block">
            <span className="block text-xs text-gray-500 mb-1">
              Call them what, if we don’t know their name
            </span>
            <input className={cls} value={draft.callerNameFallback || ''}
                   placeholder="uses the agent’s setting"
                   onChange={(e) => set({ callerNameFallback: e.target.value || null })} />
            <span className="block mt-1 text-[11px] text-gray-400">
              The name is resolved before the call is placed, so the agent is handed “Priya” or
              this word — never a template it might read out loud. Leave blank to use the agent’s.
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.oncePerContact ?? true}
                     onChange={(e) => set({ oncePerContact: e.target.checked })} />
              Only once per person
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.enabled ?? true}
                     onChange={(e) => set({ enabled: e.target.checked })} />
              Enabled
            </label>
          </div>
          <p className="text-[11px] text-gray-400 max-w-3xl">
            Leave “only once” on unless the rule should act every time. A quick-reply button is easy
            to tap more than once, and without it each tap is another call. Calls outside the
            organisation’s calling hours are held and placed when the window opens, not dropped.
          </p>

          <div className="flex gap-2">
            <button className="border rounded px-3 py-2 text-sm flex items-center gap-2 bg-white"
                    disabled={saving} onClick={save}>
              <Save size={16} /> {saving ? 'Saving…' : 'Save rule'}
            </button>
            <button className="text-sm px-3 py-2" onClick={() => { setDraft(null); setErr(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-sm font-semibold mb-2">Rules</h2>
        {rules.length === 0 ? (
          <p className="text-sm text-gray-500">No rules yet. Nothing happens automatically.</p>
        ) : (
          <div className="border rounded divide-y">
            {rules.map((r) => (
              <div key={r.id} className="p-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {r.action === 'AI_CALL' ? <PhoneCall size={14} />
                      : r.action === 'OPT_OUT' ? <Ban size={14} />
                      : <ListPlus size={14} />}
                    {r.name}
                    {!r.enabled && (
                      <span className="text-[11px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">off</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {/* WHICH campaign this watches, first — it is the difference
                        between a rule that fires on one send and one that fires on
                        every inbound message, and it was not on screen at all. */}
                    {r.campaignId ? `on ${campaignName(r.campaignId)}` : 'on any campaign'}
                    {' · '}
                    {MATCH_LABEL[r.matchType]}
                    {r.matchValue ? ` = “${r.matchValue}”` : ''}
                    {' → '}
                    {r.action === 'AI_CALL'
                      ? `call with agent ${agents.find((a: any) => String(a.id) === r.agentId)?.name || r.agentId}`
                      : r.action === 'OPT_OUT'
                      ? 'stop contacting them, every channel'
                      : `enrol in ${voiceCampaignName(r.voiceCampaignId)}`}
                    {r.oncePerContact ? ' · once per person' : ' · every time'}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button className="text-xs border rounded px-2 py-1"
                          onClick={() => { setDraft({ ...r }); setErr(null); setMsg(null); }}>
                    Edit
                  </button>
                  <button className="text-xs border rounded px-2 py-1 text-red-700"
                          onClick={() => remove(r.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-sm font-semibold mb-2">What the rules did</h2>
        <p className="text-[11px] text-gray-400 mb-2 max-w-3xl">
          Including the times they decided not to act — a person on a do-not-call list, a rule that
          had already fired for them, or a call held until the calling window opens. A list of only
          the successes would make “we called everybody who asked” impossible to check.
        </p>
        {actions.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing yet.</p>
        ) : (
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Rule</th>
                  <th className="text-left px-3 py-2">Campaign</th>
                  <th className="text-left px-3 py-2">Person</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {actions.map((a) => (
                  <tr key={a.id}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {new Date(a.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">{ruleName(a.ruleId)}</td>
                    {/* The campaign the REPLY was attributed to, which is not
                        necessarily the rule's: a rule scoped to "any campaign"
                        fires on replies from all of them, and which one it was
                        is the thing an operator wants to count. */}
                    <td className="px-3 py-2">{campaignName(a.campaignId) || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{a.contactPhone}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded ${STATUS_STYLE[a.status]}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{a.detail || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppReplyRules;
